<template>
    <div class="modal-browser-overlay d-flex align-items-center justify-content-center p-2 p-sm-3">
        <div class="donation-card shadow-2xl bg-white overflow-hidden position-relative w-100">

            <!-- HEADER ELEGANTE COM TEMA #5F0487 -->
            <div class="donation-header text-white p-3 px-sm-4 py-sm-3 position-relative">
                <button type="button"
                    class="btn-close btn-close-white position-absolute top-50 end-0 translate-middle-y me-3 opacity-75 hover-opacity-100"
                    @click="closeModal" aria-label="Fechar"></button>

                <div class="d-flex align-items-center gap-3">
                    <div
                        class="heart-icon-wrapper bg-white text-primary rounded-circle shadow-sm d-flex align-items-center justify-content-center flex-shrink-0">
                        <i class="bi bi-box2-heart-fill fs-4" style="color: #5F0487;"></i>
                    </div>
                    <div class="text-start text-truncate pe-4">
                        <span class="badge-campaign fw-semibold text-uppercase d-inline-block mb-1">
                            Apoiar Campanha
                        </span>
                        <h5 class="fw-bold mb-0 text-truncate text-white"
                            style="font-size: 1.1rem; letter-spacing: -0.3px;">
                            {{ campaign.title }}
                        </h5>
                    </div>
                </div>
            </div>

            <!-- CORPO DO MODAL COM ROLAGEM INTELIGENTE -->
            <div class="card-body p-3 p-sm-4 modal-scrollable-body">

                <!-- PASSO 1: DADOS E VALOR DA DOAÇÃO -->
                <transition name="slide-fade" mode="out-in">
                    <div v-if="step === 1" key="step1">

                        <!-- ALERTA DE ERRO -->
                        <div v-if="errorMessage"
                            class="alert alert-danger py-2 px-3 small rounded-3 mb-3 d-flex align-items-center gap-2 border-0 bg-danger-subtle text-danger">
                            <i class="bi bi-exclamation-circle-fill fs-6 flex-shrink-0"></i>
                            <span class="flex-grow-1 fw-medium" style="font-size: 0.85rem;">{{ errorMessage }}</span>
                            <button type="button" class="btn-close btn-close-sm" @click="errorMessage = null"></button>
                        </div>

                        <!-- SEÇÃO 1: VALOR -->
                        <div class="mb-3">
                            <label
                                class="form-label fw-bold text-dark mb-2 d-flex align-items-center justify-content-between"
                                style="font-size: 0.88rem;">
                                <span>Qual valor você deseja doar?</span>
                                <span class="text-muted fw-normal" style="font-size: 0.75rem;"
                                    v-if="campaign.allows_fractional">Mínimo R$ 10,00</span>
                            </label>

                            <!-- GRID DE VALORES (2x2) -->
                            <div v-if="campaign.allows_fractional" class="row row-cols-2 g-2 mb-2">
                                <div v-for="val in quickAmounts" :key="val" class="col">
                                    <button type="button"
                                        class="btn btn-outline-donation w-100 fw-bold py-2 px-3 text-nowrap"
                                        :class="{ 'active': customAmount === val && !isCustomValueFocus }"
                                        @click="selectQuickAmount(val)">
                                        R$ {{ formatCurrency(val) }}
                                    </button>
                                </div>
                                <div class="col">
                                    <button type="button"
                                        class="btn btn-outline-donation w-100 fw-bold py-2 px-3 text-nowrap"
                                        :class="{ 'active': isCustomValueFocus }" @click="focusCustomInput">
                                        Outro Valor
                                    </button>
                                </div>
                            </div>

                            <!-- INPUT DE VALOR CUSTOMIZADO -->
                            <div class="input-group shadow-sm border rounded-3 overflow-hidden custom-amount-group"
                                :class="{ 'focused': isCustomValueFocus, 'disabled': !campaign.allows_fractional }">
                                <span class="input-group-text bg-light border-0 text-muted fw-bold px-3">R$</span>
                                <input ref="customAmountInput" type="text"
                                    class="form-control border-0 fw-bold text-dark fs-5 py-2"
                                    v-model="displayCustomAmount" @input="handleCurrencyMask"
                                    @focus="isCustomValueFocus = true" :disabled="!campaign.allows_fractional"
                                    placeholder="0,00">
                            </div>
                        </div>

                        <!-- SEÇÃO 2: DADOS PESSOAIS -->
                        <div class="mb-2">
                            <label class="form-label fw-bold text-dark mb-2" style="font-size: 0.88rem;">
                                Seus Dados Pessoais
                            </label>

                            <form @submit.prevent="goToPayment" class="d-flex flex-column gap-2">
                                <div>
                                    <input type="text" v-model="donor.name" class="form-control form-control-modern"
                                        placeholder="Nome Completo" required>
                                </div>

                                <div>
                                    <input type="email" v-model="donor.email" class="form-control form-control-modern"
                                        placeholder="E-mail" required>
                                </div>

                                <div class="row g-2">
                                    <div class="col-6">
                                        <input type="text" v-model="donor.phone" @input="maskPhone"
                                            class="form-control form-control-modern" placeholder="WhatsApp"
                                            maxlength="15" required>
                                    </div>
                                    <div class="col-6">
                                        <input type="text" v-model="donor.document" @input="maskDocument"
                                            class="form-control form-control-modern" placeholder="CPF / CNPJ"
                                            maxlength="18" required>
                                    </div>
                                </div>

                                <!-- MENSAGEM DE INCENTIVO -->
                                <div>
                                    <textarea v-model="donor.comment" class="form-control form-control-modern" rows="2"
                                        placeholder="Mensagem de incentivo para a campanha (opcional)..."
                                        style="resize: none; font-size: 0.85rem;"></textarea>
                                </div>

                                <!-- CARD CHECKBOX ANÔNIMO -->
                                <!-- CHECKBOX DE DOAÇÃO ANÔNIMA -->
                                <div class="p-2.5 rounded-3 border bg-light d-flex align-items-center gap-2 mt-1">
                                    <input class="form-check-input my-0 ms-1" type="checkbox" id="anonymousCheck"
                                        v-model="donor.is_anonymous"
                                        style="cursor: pointer; width: 1.15em; height: 1.15em;">
                                    <label class="form-check-label text-secondary fw-medium small mb-0"
                                        for="anonymousCheck"
                                        style="cursor: pointer; user-select: none; font-size: 0.8rem;">
                                        Quero fazer uma doação anônima (ocultar meu nome no Mural)
                                    </label>
                                </div>

                                <!-- BOTÃO CONTINUAR -->
                                <button type="submit"
                                    class="btn btn-donation-primary w-100 fw-bold py-3 fs-6 mt-3 d-flex align-items-center justify-content-center gap-2"
                                    :disabled="!isAmountValid">
                                    <span>Continuar para Pagamento</span>
                                    <i class="bi bi-arrow-right fs-5"></i>
                                </button>
                            </form>
                        </div>

                    </div>

                    <!-- PASSO 2: ESCOLHER MÉTODO DE PAGAMENTO -->
                    <div v-else-if="step === 2" key="step2">

                        <!-- LOADING -->
                        <div v-if="processing" class="text-center py-5 animation-fade">
                            <div class="spinner-border" style="width: 3rem; height: 3rem; color: #5F0487;"
                                role="status"></div>
                            <h5 class="fw-bold text-dark mt-3">Gerando sua doação...</h5>
                            <p class="text-muted small mb-0">Aguarde um momento.</p>
                        </div>

                        <!-- SELEÇÃO DE MÉTODOS -->
                        <div v-else-if="!selectedMethod" class="animation-fade">
                            <div class="d-flex align-items-center mb-3">
                                <button
                                    class="btn btn-sm btn-light border me-3 rounded-circle d-flex align-items-center justify-content-center"
                                    style="width: 36px; height: 36px;" @click="step = 1">
                                    <i class="bi bi-arrow-left fs-6"></i>
                                </button>
                                <div>
                                    <h6 class="fw-bold text-dark mb-0">Forma de Pagamento</h6>
                                    <span class="text-muted small">Total a doar: <strong style="color: #5F0487;">R$ {{
                                        formatCurrency(customAmount) }}</strong></span>
                                </div>
                            </div>

                            <div v-if="errorMessage"
                                class="alert alert-danger py-2 px-3 small rounded-3 mb-3 d-flex align-items-center gap-2 border-0 bg-danger-subtle text-danger">
                                <i class="bi bi-exclamation-circle-fill"></i>
                                <span class="flex-grow-1">{{ errorMessage }}</span>
                                <button type="button" class="btn-close btn-close-sm"
                                    @click="errorMessage = null"></button>
                            </div>

                            <div class="d-flex flex-column gap-2.5">
                                <button class="payment-method-card d-flex align-items-center p-3 text-start w-100"
                                    @click="processDonation('pix')">
                                    <div class="icon-box bg-primary-subtle text-primary me-3">
                                        <i class="bi bi-qr-code-scan fs-4"></i>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h6 class="fw-bold text-dark mb-0">PIX</h6>
                                        <span class="text-muted small">Aprovação instantânea</span>
                                    </div>
                                    <i class="bi bi-chevron-right text-muted"></i>
                                </button>

                                <button class="payment-method-card d-flex align-items-center p-3 text-start w-100"
                                    @click="processDonation('boleto')">
                                    <div class="icon-box bg-info-subtle text-info me-3">
                                        <i class="bi bi-upc-scan fs-4"></i>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h6 class="fw-bold text-dark mb-0">Boleto Bancário</h6>
                                        <span class="text-muted small">Aprovação em até 2 dias úteis</span>
                                    </div>
                                    <i class="bi bi-chevron-right text-muted"></i>
                                </button>

                                <button class="payment-method-card d-flex align-items-center p-3 text-start w-100"
                                    @click="selectedMethod = 'credit_card'">
                                    <div class="icon-box bg-success-subtle text-success me-3">
                                        <i class="bi bi-credit-card-fill fs-4"></i>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h6 class="fw-bold text-dark mb-0">Cartão de Crédito</h6>
                                        <span class="text-muted small">Pague de forma segura</span>
                                    </div>
                                    <i class="bi bi-chevron-right text-muted"></i>
                                </button>
                            </div>
                        </div>

                        <!-- FORMULÁRIO DO CARTÃO -->
                        <div v-if="selectedMethod === 'credit_card' && !processing" class="animation-fade">
                            <div class="d-flex align-items-center mb-3">
                                <button
                                    class="btn btn-sm btn-light border me-3 rounded-circle d-flex align-items-center justify-content-center"
                                    style="width: 36px; height: 36px;" @click="selectedMethod = null">
                                    <i class="bi bi-arrow-left fs-6"></i>
                                </button>
                                <div>
                                    <h6 class="fw-bold text-dark mb-0">Cartão de Crédito</h6>
                                    <span class="text-muted small">R$ {{ formatCurrency(customAmount) }}</span>
                                </div>
                            </div>

                            <div v-if="errorMessage"
                                class="alert alert-danger py-2 px-3 small rounded-3 mb-3 d-flex align-items-center gap-2 border-0 bg-danger-subtle text-danger">
                                <i class="bi bi-exclamation-circle-fill fs-6"></i>
                                <span class="flex-grow-1 fw-medium">{{ errorMessage }}</span>
                                <button type="button" class="btn-close btn-close-sm"
                                    @click="errorMessage = null"></button>
                            </div>

                            <!-- CARTÃO PREVIEW 3D -->
                            <div class="card-preview-container mb-3 mt-1">
                                <div class="card-inner" :class="{ 'is-flipped': isFlipped }">
                                    <div class="card-front shadow-sm border">
                                        <div class="d-flex justify-content-between align-items-start mb-2">
                                            <div class="chip"></div>
                                            <div v-if="cardBrand === 'visa'" class="css-logo-visa">VISA</div>
                                            <div v-else-if="cardBrand === 'mastercard'" class="css-logo-mastercard">
                                                <div class="mc-red"></div>
                                                <div class="mc-orange"></div>
                                            </div>
                                            <i v-else class="bi bi-credit-card fs-3 text-white opacity-75"></i>
                                        </div>
                                        <div class="card-number-display mb-2 fs-6">{{ cardForm.number || '**** **** **** ****' }}</div>
                                        <div class="d-flex justify-content-between">
                                            <div class="text-truncate me-2">
                                                <span class="d-block opacity-50" style="font-size: 8px;">TITULAR</span>
                                                <span class="fw-medium text-uppercase" style="font-size: 11px;">{{
                                                    cardForm.name || 'NOME IMPRESSO' }}</span>
                                            </div>
                                            <div class="text-end">
                                                <span class="d-block opacity-50" style="font-size: 8px;">VALIDADE</span>
                                                <span class="fw-medium" style="font-size: 11px;">{{ cardForm.expiry ||
                                                    'MM/AA' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-back shadow-sm border">
                                        <div class="magnetic-strip mt-3"></div>
                                        <div class="px-3 mt-3 text-end">
                                            <span class="d-block text-white opacity-75 mb-1"
                                                style="font-size: 9px;">CVV</span>
                                            <div
                                                class="cvv-display text-dark bg-white rounded-1 px-2 py-1 fw-bold fs-6 d-inline-block">
                                                {{ cardForm.cvv || '***' }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form @submit.prevent="processDonation('credit_card')" class="d-flex flex-column gap-2">
                                <div>
                                    <input type="text" v-model="cardForm.number" @input="maskCardNumber"
                                        class="form-control form-control-modern font-monospace"
                                        placeholder="Número do Cartão" maxlength="19" required>
                                </div>
                                <div>
                                    <input type="text" v-model="cardForm.name"
                                        class="form-control form-control-modern text-uppercase"
                                        placeholder="Nome Impresso no Cartão" required>
                                </div>
                                <div class="row g-2">
                                    <div class="col-6">
                                        <input type="text" v-model="cardForm.expiry" @input="maskExpiry"
                                            class="form-control form-control-modern font-monospace"
                                            placeholder="Validade (MM/AA)" maxlength="5" required>
                                    </div>
                                    <div class="col-6">
                                        <input type="text" v-model="cardForm.cvv" @input="maskCvv"
                                            @focus="isFlipped = true" @blur="isFlipped = false"
                                            class="form-control form-control-modern font-monospace" placeholder="CVV"
                                            maxlength="4" required>
                                    </div>
                                </div>

                                <button type="submit" class="btn btn-donation-primary w-100 fw-bold py-3 mt-2">
                                    <i class="bi bi-lock-fill me-2"></i> Pagar R$ {{ formatCurrency(customAmount) }}
                                </button>
                            </form>
                        </div>

                    </div>

                    <!-- PASSO 3: SUCESSO / PIX / BOLETO GERADO -->
                    <div v-else-if="step === 3" key="step3" class="text-center animation-fade py-2">

                        <!-- CARTÃO SUCESSO -->
                        <div v-if="selectedMethod === 'credit_card'">
                            <div class="success-checkmark mb-3 mx-auto d-flex align-items-center justify-content-center bg-success-subtle text-success rounded-circle"
                                style="width: 70px; height: 70px;">
                                <i class="bi bi-check-lg fs-1"></i>
                            </div>
                            <h4 class="fw-bold text-dark mb-2">Muito Obrigado!</h4>
                            <p class="text-muted small">Sua doação de <strong class="text-dark">R$ {{
                                formatCurrency(customAmount) }}</strong> foi aprovada. Agradecemos o seu apoio!</p>
                            <button class="btn btn-outline-dark fw-bold mt-2 px-4 py-2 rounded-pill"
                                @click="closeModal">Voltar para a página</button>
                        </div>

                        <!-- PIX -->
                        <div v-else-if="selectedMethod === 'pix'">
                            <h5 class="fw-bold text-dark mb-1">Pague via PIX</h5>
                            <p class="small text-muted mb-3">Leia o QR Code ou copie o código abaixo.</p>

                            <div class="qr-code-box bg-light border p-2.5 rounded-4 mx-auto mb-3 shadow-sm"
                                style="max-width: 180px;">
                                <img :src="'data:image/jpeg;base64,' + pixData.qr_code_base64" alt="QR Code PIX"
                                    class="img-fluid rounded-3">
                            </div>

                            <div class="input-group shadow-sm border rounded-3 mb-2">
                                <input type="text" class="form-control border-0 bg-white text-muted small"
                                    :value="pixData.copy_paste" readonly>
                                <button class="btn btn-dark fw-bold px-3"
                                    @click="copyText(pixData.copy_paste)">COPIAR</button>
                            </div>

                            <transition name="fade">
                                <div v-if="textCopied" class="text-success small fw-bold mb-2">
                                    <i class="bi bi-check-circle-fill me-1"></i> Chave PIX copiada!
                                </div>
                            </transition>

                            <button class="btn btn-link text-muted text-decoration-none small mt-1"
                                @click="closeModal">Fechar janela</button>
                            <button class="btn btn-link text-danger text-decoration-none small mt-1 d-block mx-auto"
                                @click="clearPendingDonation">
                                <i class="bi bi-arrow-counterclockwise me-1"></i> Cancelar e gerar nova doação
                            </button>
                        </div>

                        <!-- BOLETO -->
                        <div v-else-if="selectedMethod === 'boleto'">
                            <div class="mb-3 mx-auto d-flex align-items-center justify-content-center bg-info-subtle text-info rounded-circle"
                                style="width: 60px; height: 60px;">
                                <i class="bi bi-upc-scan fs-2"></i>
                            </div>
                            <h5 class="fw-bold text-dark mb-1">Boleto Gerado!</h5>
                            <p class="small text-muted mb-3">Copie a linha digitável ou baixe o PDF.</p>

                            <div class="p-2.5 bg-light border rounded-3 mb-3 font-monospace fw-bold text-break fs-6">
                                {{ boletoData.digitable_line }}
                            </div>

                            <div class="d-flex gap-2 mb-2">
                                <button class="btn btn-dark fw-bold flex-grow-1 py-2 rounded-3"
                                    @click="copyText(boletoData.digitable_line)">
                                    <i class="bi bi-copy me-1"></i> Copiar Linha
                                </button>
                                <a :href="boletoData.bank_slip_url" target="_blank"
                                    class="btn btn-outline-dark fw-bold flex-grow-1 py-2 rounded-3">
                                    <i class="bi bi-file-earmark-pdf me-1"></i> Ver PDF
                                </a>
                            </div>

                            <transition name="fade">
                                <div v-if="textCopied" class="text-success small fw-bold mb-2">
                                    <i class="bi bi-check-circle-fill me-1"></i> Linha copiada!
                                </div>
                            </transition>

                            <button class="btn btn-link text-muted text-decoration-none small mt-1"
                                @click="closeModal">Fechar janela</button>
                            <button class="btn btn-link text-danger text-decoration-none small mt-1 d-block mx-auto"
                                @click="clearPendingDonation">
                                <i class="bi bi-arrow-counterclockwise me-1"></i> Cancelar e gerar nova doação
                            </button>
                        </div>

                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    name: 'DonationModal',
    props: {
        campaign: {
            type: Object,
            required: true,
            default: () => ({ title: 'Campanha de Arrecadação', amount: 50, allows_fractional: true })
        },
        isPreview: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            step: 1,
            processing: false,
            quickAmounts: [25, 50, 100],
            customAmount: 0,
            displayCustomAmount: '',
            isCustomValueFocus: false,

            donor: {
                name: '',
                email: '',
                phone: '',
                document: '',
                comment: '',
                is_anonymous: false
            },

            selectedMethod: null,
            isFlipped: false,
            cardForm: {
                number: '',
                name: '',
                expiry: '',
                cvv: ''
            },

            pixData: { qr_code_base64: '', copy_paste: '' },
            boletoData: { digitable_line: '', bank_slip_url: '' },
            textCopied: false,
            errorMessage: null,
        }
    },
    computed: {
        isAmountValid() {
            return this.customAmount >= 10;
        },
        cardBrand() {
            if (!this.cardForm.number) return null;
            const num = this.cardForm.number.replace(/\D/g, '');
            if (/^4/.test(num)) return 'visa';
            if (/^(5[1-5]|2[2-7])/.test(num)) return 'mastercard';
            return null;
        }
    },
    mounted() {
        this.customAmount = parseFloat(this.campaign.amount) || 50;
        this.displayCustomAmount = this.formatCurrencyInput(this.customAmount);
        this.checkPendingDonation();
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        selectQuickAmount(val) {
            this.isCustomValueFocus = false;
            this.customAmount = val;
            this.displayCustomAmount = this.formatCurrencyInput(val);
        },
        focusCustomInput() {
            this.isCustomValueFocus = true;
            setTimeout(() => this.$refs.customAmountInput?.focus(), 100);
        },
        handleCurrencyMask(event) {
            let value = event.target.value.replace(/\D/g, "");
            if (value === "") { this.customAmount = 0; this.displayCustomAmount = ""; return; }
            let numericValue = parseInt(value, 10) / 100;
            this.displayCustomAmount = numericValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            this.customAmount = numericValue;
        },
        goToPayment() {
            if (this.isAmountValid && this.donor.name && this.donor.email && this.donor.document) {
                this.step = 2;
            }
        },

        async processDonation(method) {
            if (this.isPreview) {
                alert("Modo Preview: O doador será redirecionado para o pagamento real aqui.");
                this.$emit('close');
                return;
            }

            if (method === 'credit_card' && this.selectedMethod !== 'credit_card') {
                this.selectedMethod = 'credit_card';
                return;
            }

            this.selectedMethod = method;
            this.processing = true;
            this.errorMessage = null;

            try {
                if (method === 'credit_card') {
                    const [expiryMonth, expiryYearShort] = this.cardForm.expiry.split('/');
                    const expiryYear = expiryYearShort?.length === 2 ? `20${expiryYearShort}` : expiryYearShort;

                    const payload = {
                        landing_page_id: this.campaign.id,
                        payment_method: 'credit_card',
                        amount: this.customAmount,
                        name_client: this.donor.name,
                        email_client: this.donor.email,
                        phone_client: this.donor.phone.replace(/\D/g, ''),
                        document_client: this.donor.document.replace(/\D/g, ''),
                        comment: this.donor.comment,
                        is_anonymous: this.donor.is_anonymous,

                        card_number: this.cardForm.number.replace(/\D/g, ''),
                        card_holder_name: this.cardForm.name,
                        card_expiry_month: expiryMonth,
                        card_expiry_year: expiryYear,
                        card_cvv: this.cardForm.cvv,

                        holder_email: this.donor.email,
                        holder_cpf_cnpj: this.donor.document.replace(/\D/g, ''),
                        holder_phone: this.donor.phone.replace(/\D/g, ''),
                        holder_postal_code: '00000000',
                        holder_address_number: 'SN'
                    };

                    const response = await api.post('/donations/credit-card', payload);

                    if (response.data.success || response.data.data) {
                        this.step = 3;
                    }

                } else {
                    const payload = {
                        landing_page_id: this.campaign.id,
                        payment_method: method,
                        amount: this.customAmount,
                        name_client: this.donor.name,
                        email_client: this.donor.email,
                        phone_client: this.donor.phone.replace(/\D/g, ''),
                        document_client: this.donor.document.replace(/\D/g, ''),
                        comment: this.donor.comment,
                        is_anonymous: this.donor.is_anonymous
                    };

                    const response = await api.post('/donations/charges', payload);
                    const responseData = response.data.data || response.data;
                    const metadata = responseData.gateway_metadata || {};

                    if (method === 'pix') {
                        this.pixData = {
                            qr_code_base64: metadata.pix_qr_code_url || '',
                            copy_paste: metadata.pix_copy_paste || ''
                        };
                    } else if (method === 'boleto') {
                        this.boletoData = {
                            digitable_line: metadata.digitable_line || 'Linha digitável indisponível',
                            bank_slip_url: metadata.bank_slip_url || '#'
                        };
                    }

                    this.step = 3;
                    if (method === 'pix' || method === 'boleto') {
                        this.savePendingDonation(method);
                    }
                }

            } catch (error) {
                console.error("Erro ao processar doação:", error);
                const status = error.response?.status;
                const backendMsg = error.response?.data?.message;

                if (status === 400 || status === 422) {
                    this.errorMessage = backendMsg || 'Verifique os dados informados e tente novamente.';
                } else {
                    this.errorMessage = 'Não foi possível concluir a sua solicitação no momento. Tente novamente mais tarde.';
                }

                if (method !== 'credit_card') {
                    this.selectedMethod = null;
                }
            } finally {
                this.processing = false;
            }
        },

        async copyText(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.textCopied = true;
                setTimeout(() => this.textCopied = false, 3000);
            } catch (err) { console.error(err) }
        },

        formatCurrency(value) {
            return parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatCurrencyInput(value) {
            return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        maskCardNumber(e) { let val = e.target.value.replace(/\D/g, ''); this.cardForm.number = val.replace(/(\d{4})(?=\d)/g, '$1 '); },
        maskExpiry(e) { let val = e.target.value.replace(/\D/g, ''); this.cardForm.expiry = val.replace(/(\d{2})(\d)/, '$1/$2'); },
        maskCvv(e) { this.cardForm.cvv = e.target.value.replace(/\D/g, ''); },
        maskPhone(e) {
            let val = e.target.value.replace(/\D/g, '');
            if (val.length <= 10) val = val.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
            else val = val.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
            this.donor.phone = val;
        },
        maskDocument(e) {
            let val = e.target.value.replace(/\D/g, '');
            if (val.length <= 11) val = val.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
            else val = val.substring(0, 14).replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
            this.donor.document = val;
        },
        savePendingDonation(method) {
            const pendingData = {
                method: method,
                amount: this.customAmount,
                pixData: this.pixData,
                boletoData: this.boletoData,
                // Registra o momento exato em que foi gerado
                timestamp: new Date().getTime()
            };
            localStorage.setItem(`rmr_pending_${this.campaign.id}`, JSON.stringify(pendingData));
        },
        checkPendingDonation() {
            const saved = localStorage.getItem(`rmr_pending_${this.campaign.id}`);
            if (saved) {
                const data = JSON.parse(saved);
                const now = new Date().getTime();
                const diffMinutes = Math.floor((now - data.timestamp) / 1000 / 60);

                // Se tiver menos de 30 minutos, recupera a sessão e joga pro Passo 3
                if (diffMinutes < 30) {
                    this.selectedMethod = data.method;
                    this.customAmount = data.amount;
                    this.pixData = data.pixData;
                    this.boletoData = data.boletoData;
                    this.step = 3;
                } else {
                    // Se expirou (mais de 30 min), limpa o cache
                    this.clearPendingDonation();
                }
            }
        },

        clearPendingDonation() {
            localStorage.removeItem(`rmr_pending_${this.campaign.id}`);
            this.step = 1;
            this.selectedMethod = null;
            this.pixData = { qr_code_base64: '', copy_paste: '' };
            this.boletoData = { digitable_line: '', bank_slip_url: '' };
        },
    }
}
</script>

<style scoped>
.modal-browser-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(15, 23, 42, 0.75);
    z-index: 9999;
    backdrop-filter: blur(6px);
    overflow-y: auto;
}

.donation-card {
    max-width: 470px;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    margin: auto;
}

.donation-header {
    background: linear-gradient(135deg, #5F0487 0%, #41025e 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.badge-campaign {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 50px;
    padding: 2px 8px;
    font-size: 0.65rem;
    letter-spacing: 0.5px;
}

.heart-icon-wrapper {
    width: 42px;
    height: 42px;
}

.modal-scrollable-body {
    max-height: 80vh;
    overflow-y: auto;
}

.modal-scrollable-body::-webkit-scrollbar {
    width: 5px;
}

.modal-scrollable-body::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 10px;
}

/* --- BOTÕES E INPUTS --- */
.btn-outline-donation {
    border: 1.5px solid #e2e8f0;
    color: #475569;
    background: #ffffff;
    border-radius: 10px;
    font-size: 0.88rem;
    transition: all 0.2s ease;
}

.btn-outline-donation:hover {
    border-color: #5F0487;
    color: #5F0487;
    background: rgba(95, 4, 135, 0.04);
}

.btn-outline-donation.active {
    border-color: #5F0487;
    color: #ffffff;
    background: #5F0487;
    box-shadow: 0 4px 10px rgba(95, 4, 135, 0.25);
}

.custom-amount-group {
    transition: all 0.2s ease;
    border-color: #e2e8f0 !important;
}

.custom-amount-group.focused {
    border-color: #5F0487 !important;
    box-shadow: 0 0 0 3px rgba(95, 4, 135, 0.15) !important;
}

.form-control-modern {
    padding: 0.65rem 0.85rem;
    font-size: 0.9rem;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background-color: #f8fafc;
    transition: all 0.2s ease;
}

.form-control-modern:focus {
    background-color: #ffffff;
    border-color: #5F0487;
    box-shadow: 0 0 0 3px rgba(95, 4, 135, 0.12);
    outline: none;
}

.btn-donation-primary {
    background-color: #5F0487;
    color: #ffffff;
    border: none;
    border-radius: 12px;
    transition: all 0.25 ease-in-out;
}

.btn-donation-primary:hover:not(:disabled) {
    background-color: #4c036c;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(95, 4, 135, 0.3);
}

.payment-method-card {
    background-color: #ffffff;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    transition: all 0.2s ease;
    cursor: pointer;
}

.payment-method-card:hover {
    border-color: #5F0487;
    background-color: rgba(95, 4, 135, 0.02);
    transform: translateY(-1px);
}

.icon-box {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* ANIMATION SLIDE FADE */
.slide-fade-enter-active {
    transition: all 0.25s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.15s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateX(15px);
    opacity: 0;
}

.animation-fade {
    animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(6px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* CARTÃO 3D PREVIEW */
.card-preview-container {
    perspective: 1000px;
    width: 100%;
    max-width: 290px;
    height: 170px;
    margin: 0 auto;
}

.card-inner {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform-style: preserve-3d;
}

.card-inner.is-flipped {
    transform: rotateY(180deg);
}

.card-front,
.card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    border-radius: 14px;
    padding: 16px;
    color: white;
}

.card-front {
    background: linear-gradient(135deg, #5F0487 0%, #101930 100%);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.card-back {
    background: linear-gradient(135deg, #1a202c 0%, #000000 100%);
    transform: rotateY(180deg);
    padding: 0;
}

.chip {
    width: 38px;
    height: 28px;
    background: linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%);
    border-radius: 5px;
}

.card-number-display {
    font-family: 'Courier New', Courier, monospace;
    letter-spacing: 1.5px;
}

.magnetic-strip {
    width: 100%;
    height: 36px;
    background: #000;
}

.css-logo-visa {
    font-family: Arial, sans-serif;
    font-weight: 900;
    font-style: italic;
    font-size: 22px;
    color: #fff;
}

.css-logo-mastercard {
    display: flex;
    align-items: center;
}

.mc-red,
.mc-orange {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}

.mc-red {
    background-color: #eb001b;
    z-index: 2;
}

.mc-orange {
    background-color: #f79e1b;
    margin-left: -10px;
    z-index: 1;
    opacity: 0.9;
}
</style>